import type { ParsedUrlQuery } from "querystring";

const SECURED_PREFIX = "/s/";
const SECURED_PREFIX_REGEXP = /^\/s\//;

export interface RouteInfo {
  /** Path of current route without suffixes */
  base: string;
  /** Page number if pagination was detected */
  page: number | null;
  query?: ParsedUrlQuery;
}

/**
 * Detect invalid slug characters in an url path
 *
 * NOTE: A-Z and _ are not valid in newer slugs but some legacy slug still contain these characters.
 *
 * [^ ... ]: A negated character class that matches any character not listed within the brackets.
 * .: A dot is allowed.
 * a-zA-Z: All uppercase and lowercase English letters are allowed.
 * 0-9: All digits are allowed.
 * /_-: The characters /, _, and - are allowed.
 * À-ÖØ-öø-ÿ: Allow common accented characters
 * ': Allow for apostrophe, for example in names such as James D'Amato
 * +: One or more occurrences of the preceding pattern.
 */
export const INVALID_SLUG_CHARACTERS_REGEXP = /[^ .a-zA-Z0-9/_-À-ÖØ-öø-ÿ']+/;
/**
 * Resource paths should never have a file extension. JS files have been found to
 * be requested via ad related code, causing invalid resource requests to WordPress.
 */
export const INVALID_FILE_EXTENSION_REGEXP =
  /\.(js|jsx|ts|tsx|html|php|asp|css)$/i;

/**
 * Validate path for invalid characters and file extensions
 */
export function validatePath(path: string): boolean {
  let decodedPath: string;

  try {
    decodedPath = decodeURIComponent(path);
  } catch (error) {
    if (error instanceof URIError) {
      return false;
    }
    throw error;
  }

  return (
    !INVALID_SLUG_CHARACTERS_REGEXP.test(decodedPath) &&
    !INVALID_FILE_EXTENSION_REGEXP.test(decodedPath)
  );
}
/**
 * Regexp to detect In-Stream Suffix that is no longer supported.
 */
export const LEGACY_IN_STREAM_SUFFIX_REGEXP = /^(.*)\/in\/[0-9]+\/?$/;

/**
 * Regexp to detect Comment Suffix that is no longer supported.
 */
export const LEGACY_COMMENT_SUFFIX_REGEXP = /^(.*)\/comment\/[0-9]+\/?$/;

/**
 * Regexp to detect pagination without valid page number.
 */
export const PAGINATION_SUFFIX_INVALID_PAGE_REGEXP =
  /^(.*)\/archives((\/?)|(\/0))$/;

/**
 * Regexp to detect pagination. Allows for optional query string.
 */
const PAGINATION_SUFFIX_REGEXP = /^(.*)\/archives\/([0-9]+)\/?(\?.*)?$/;

/**
 * Given a `pathname` detect legacy patterns and return a redirect path
 * or false.
 * @returns redirectPath or false
 */
export function detectLegacyPatternRedirect(pathname: string): string | false {
  let match: RegExpMatchArray | null = null;

  // Remove legacy In-Stream Suffixes.
  match = pathname.match(LEGACY_IN_STREAM_SUFFIX_REGEXP);
  if (match) {
    return `${match[1] as string}`;
  }

  // Remove legacy Comment Suffixes.
  match = pathname.match(LEGACY_COMMENT_SUFFIX_REGEXP);
  if (match) {
    return `${match[1] as string}`;
  }

  // Redirect pagination without valid page number.
  match = pathname.match(PAGINATION_SUFFIX_INVALID_PAGE_REGEXP);
  if (match) {
    return `${match[1] as string}/${generatePaginationSuffix(2)}`;
  }

  if (pathname.startsWith(SECURED_PREFIX)) {
    return `/${pathname.replace(SECURED_PREFIX_REGEXP, "")}`;
  }
  return false;
}

/**
 * Return suffix for pagination that can be appended to url paths.
 * @returns suffix
 */
export function generatePaginationSuffix(page: number) {
  return `archives/${page}`;
}

export function parsePagination(
  pathname: string,
): { base: string; page: number } | null {
  let match: RegExpMatchArray | null = null;
  match = pathname.match(PAGINATION_SUFFIX_REGEXP);

  if (match) {
    return {
      base: match[1] as string,
      page: Number.parseInt(match[2] as string),
    };
  }
  return null;
}

export function ensureLeadingSlash(path: string) {
  if (path[0] !== "/") {
    return `/${path}`;
  }
  return path;
}

export function ensureTrailingSlash(path: string) {
  if (path[path.length - 1] !== "/") {
    return `${path}/`;
  }
  return path;
}

/**
 * Return route information from path.
 */
export function getRouteInfo(url: string): RouteInfo {
  const u = new URL(url, "http://localhost");
  const pagination = parsePagination(u.pathname);
  return {
    base: ensureLeadingSlash(pagination ? pagination.base : u.pathname),
    page: pagination ? pagination.page : null,
    query: Object.fromEntries(u.searchParams.entries()),
  };
}

export function generateFullArchiveLink(params: {
  year: number;
  month?: number;
  pageNumber?: number;
  category?: string;
}) {
  return `/archives/${params.category ? params.category + "/" : ""}${
    params.year
  }/${params.month || 1}/${params.pageNumber || 1}`;
}
